import React, { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";

const ActiveExam = ({ 
  questions = [], 
  timeLimit = 0, 
  title, 
  onExit, 
  onComplete 
}) => {
  const normalizeText = (value) =>
    String(value ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();

  const extractChoiceLetter = (value) => {
    const text = String(value ?? "").trim();
    if (/^[A-H]$/i.test(text)) return text.toUpperCase();
    const match = text.match(/^([A-H])(?:\s*[:.)-]\s*|\s+)/i);
    return match ? match[1].toUpperCase() : null;
  };

  const stripChoicePrefix = (value) =>
    String(value ?? "")
      .trim()
      .replace(/^([A-H])(?:\s*[:.)-]\s*|\s+)/i, "")
      .trim();

  const isAnswerCorrect = (question, userAnswerRaw) => {
    const userAnswer = String(userAnswerRaw ?? "").trim();
    if (!userAnswer) return false;

    const answerLetter = extractChoiceLetter(question?.answer);
    if (answerLetter) {
      let selectedLetter = extractChoiceLetter(userAnswer);
      if (!selectedLetter && question?.options?.length > 0) {
        const selectedIndex = (question.options || []).indexOf(userAnswer);
        if (selectedIndex >= 0 && selectedIndex < 8) {
          selectedLetter = String.fromCharCode(65 + selectedIndex);
        }
      }

      if (selectedLetter && selectedLetter === answerLetter) return true;
    }

    const normalizedUser = normalizeText(stripChoicePrefix(userAnswer));
    const normalizedCorrect = normalizeText(stripChoicePrefix(question?.answer));
    return normalizedUser === normalizedCorrect;
  };

  // --- STATE ---
  const [hasStarted, setHasStarted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(timeLimit === 0 ? null : timeLimit * 60);
  
  // Security State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [violations, setViolations] = useState(0);
  const containerRef = useRef(null);

  // --- HELPERS ---
  const formatTime = (sec) => {
    if (sec == null) return "∞";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const currentQ = useMemo(() => {
    if (!questions.length) return null;
    return questions[currentIndex];
  }, [questions, currentIndex]);

  // ✅ CHECK: Is the current question answered?
  const isCurrentAnswered = useMemo(() => {
      const ans = answers[currentIndex];
      // Check if not undefined, not null, and if string, not empty
      return ans !== undefined && ans !== null && String(ans).trim().length > 0;
  }, [answers, currentIndex]);

  // --- LOGIC ---
  const handleSelectAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [currentIndex]: value }));
  };

  const nextQ = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  // ✅ UPDATED: Calculate exact details needed for the backend schema
  const calculateScoreDetails = () => {
    const total = questions.length;
    let correct = 0;
    const attempts = [];

    questions.forEach((q, idx) => {
      const userAns = answers[idx];
      const isCorrect = isAnswerCorrect(q, userAns);
      
      if (isCorrect) correct++;
      
      attempts.push({ questionIndex: idx, isCorrect });
    });
    
    const percentage = total ? Math.round((correct / total) * 100) : 0;
    
    return { correct, total, percentage, attempts };
  };

  // ✅ UPDATED: Pass full payload up to parent
  const handleSubmit = () => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      const exitFs = document.exitFullscreen || document.webkitExitFullscreen;
      if (exitFs) exitFs.call(document).catch(err => console.log(err));
    }
    
    const details = calculateScoreDetails();
    
    // Calculate time spent (if timeLeft is null, it means 'No Limit' was selected)
    const timeSpentSeconds = timeLeft === null 
        ? 0 
        : (timeLimit * 60) - Math.max(0, timeLeft);

    onComplete({ ...details, timeSpentSeconds });
  };

  // --- SECURITY: FULLSCREEN TRIGGER ---
  const enterFullscreen = () => {
    if (!containerRef.current) return;
    
    const element = containerRef.current;
    const requestMethod = element.requestFullscreen || 
                          element.webkitRequestFullscreen || 
                          element.mozRequestFullScreen || 
                          element.msRequestFullscreen;

    if (requestMethod) {
      requestMethod.call(element).then(() => {
          setIsFullscreen(true);
          setHasStarted(true);
      }).catch((err) => {
          console.error(err);
          // Fallback
          setHasStarted(true);
          setIsFullscreen(true);
      });
    } else {
        setHasStarted(true);
        setIsFullscreen(true);
    }
  };

  // --- SECURITY: EVENT LISTENERS ---
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hasStarted) {
        setViolations(v => v + 1);
        Swal.fire({
          title: "⚠️ Warning!",
          text: "You switched tabs! This violation has been recorded.",
          icon: "warning",
          confirmButtonText: "I understand",
          confirmButtonColor: "#4f46e5", // Indigo
        });
      }
    };

    const handleFullscreenChange = () => {
      const isFs = document.fullscreenElement || 
                   document.webkitFullscreenElement || 
                   document.mozFullScreenElement || 
                   document.msFullscreenElement;

      if (hasStarted) {
          setIsFullscreen(!!isFs);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, [hasStarted]);

  // --- TIMER EFFECT ---
  useEffect(() => {
    if (!hasStarted || timeLeft === null) return;
    if (timeLeft === 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, hasStarted]);


  // --- MAIN RENDER ---
  return (
    <div ref={containerRef} className="bg-slate-50 min-h-screen w-full flex flex-col font-sans">
        
        {/* SCENE 1: START SCREEN */}
        {!hasStarted && (
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="bg-white max-w-md w-full p-8 rounded-3xl shadow-xl text-center border border-slate-100 animate-in zoom-in-95 duration-300">
                    <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-6">
                        <i className="fa-solid fa-expand"></i>
                    </div>
                    <h2 className="text-2xl font-black text-slate-800 mb-2">Ready to Start?</h2>
                    <p className="text-slate-500 mb-8">
                        This exam requires <strong>Fullscreen Mode</strong>. 
                        Switching tabs or exiting fullscreen will be recorded as a violation.
                    </p>
                    {/* ✅ INDIGO BUTTON (Core Theme) */}
                    <button 
                        onClick={enterFullscreen}
                        className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/30 hover:scale-[1.02] cursor-pointer"
                    >
                        Enter Fullscreen & Begin
                    </button>
                    <button 
                        onClick={onExit}
                        className="mt-4 text-slate-400 font-bold hover:text-slate-600 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        )}

        {/* SCENE 2: LOCKED SCREEN */}
        {hasStarted && !isFullscreen && (
            <div className="fixed inset-0 z-[100] bg-slate-900 flex flex-col items-center justify-center text-white p-8 text-center animate-in fade-in duration-300">
                <div className="w-24 h-24 bg-indigo-500 rounded-full flex items-center justify-center text-5xl mb-6 shadow-xl shadow-indigo-500/20 animate-pulse">
                    <i className="fa-solid fa-lock"></i>
                </div>
                <h2 className="text-4xl font-black mb-4">Exam Paused</h2>
                <p className="text-slate-300 text-lg mb-10 max-w-md leading-relaxed">
                    You have exited full-screen mode. To continue your assessment, you must return to full-screen.
                </p>
                <button 
                    onClick={enterFullscreen}
                    className="px-10 py-5 bg-white text-slate-900 rounded-2xl font-bold text-lg hover:bg-indigo-50 hover:text-indigo-900 transition-all shadow-xl hover:scale-105"
                >
                    Return to Exam
                </button>
                <button 
                    onClick={onExit}
                    className="mt-12 text-sm text-slate-500 hover:text-white transition-colors underline decoration-slate-600 underline-offset-4"
                >
                    Quit Exam
                </button>
            </div>
        )}

        {/* SCENE 3: ACTIVE EXAM UI */}
        {hasStarted && isFullscreen && (
            <div className="flex-1 w-full overflow-y-auto pb-20">
                <div className="max-w-3xl mx-auto py-10 px-4 md:px-0 animate-in fade-in zoom-in-95 duration-300">
                
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8 sticky top-0 bg-slate-50/95 backdrop-blur py-4 z-30 transition-all">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={onExit} 
                                className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-800 flex items-center justify-center transition"
                            >
                                <i className="fa-solid fa-arrow-left"></i>
                            </button>
                            <div className="h-2 w-24 md:w-48 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                className="h-full bg-indigo-600 transition-all duration-300" 
                                style={{width: `${((currentIndex + 1) / questions.length) * 100}%`}}
                                ></div>
                            </div>
                            <span className="text-sm font-bold text-slate-400">
                                {currentIndex + 1} / {questions.length}
                            </span>
                        </div>

                        <div className="flex items-center gap-3">
                            {violations > 0 && (
                                <div className="hidden sm:flex px-3 py-2 bg-red-100 text-red-600 rounded-lg text-xs font-bold items-center gap-2 border border-red-200 animate-pulse">
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                    {violations} Violation{violations > 1 ? 's' : ''}
                                </div>
                            )}
                            <div className={`px-4 py-2 rounded-lg font-mono font-bold text-lg ${
                                timeLeft !== null && timeLeft < 60 
                                    ? 'bg-red-50 text-red-600 animate-pulse' 
                                    : 'bg-white text-slate-700 shadow-sm'
                            }`}>
                                {formatTime(timeLeft)}
                            </div>
                        </div>
                    </div>

                    {/* Question Card */}
                    <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.1)] border border-slate-100">
                        <h2 className="text-2xl md:text-3xl font-black text-slate-800 leading-tight mb-8">
                        {currentQ.question}
                        </h2>

                        <div className="space-y-4">
                        {currentQ.options && currentQ.options.length > 0 ? (
                            currentQ.options.map((opt) => {
                            const isSelected = answers[currentIndex] === opt;
                            return (
                                <button
                                key={opt}
                                onClick={() => handleSelectAnswer(opt)}
                                className={`w-full text-left p-5 rounded-xl border-2 font-semibold text-lg transition-all duration-200 ${
                                    isSelected
                                    ? "border-indigo-600 bg-indigo-50 text-indigo-800 shadow-inner"
                                    : "border-slate-100 bg-white text-slate-600 hover:border-indigo-200 hover:bg-slate-50 hover:shadow-md"
                                }`}
                                >
                                <div className="flex items-start gap-3">
                                    <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-indigo-600" : "border-slate-300"}`}>
                                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-indigo-600"></div>}
                                    </div>
                                    {opt}
                                </div>
                                </button>
                            );
                            })
                        ) : (
                            <textarea
                            className="w-full p-5 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-0 text-lg min-h-[150px]"
                            placeholder="Type your answer here..."
                            value={answers[currentIndex] || ""}
                            onChange={(e) => handleSelectAnswer(e.target.value)}
                            />
                        )}
                        </div>
                    </div>

                    {/* ✅ FOOTER: No Previous Button, Enforced "Answer to Proceed" */}
                    <div className="flex justify-end mt-8 pb-10"> 
                        {currentIndex < questions.length - 1 ? (
                        <button 
                            onClick={nextQ} 
                            disabled={!isCurrentAnswered} // 🔒 LOCKED until answered
                            className={`px-8 py-4 rounded-xl font-bold transition-all shadow-lg ${
                                isCurrentAnswered 
                                ? "bg-indigo-600 text-white shadow-indigo-600/20 hover:bg-indigo-700 hover:-translate-y-1"
                                : "bg-slate-200 text-slate-400 cursor-not-allowed"
                            }`}
                        >
                            Next Question <i className="fa-solid fa-arrow-right ml-2"></i>
                        </button>
                        ) : (
                        <button 
                            onClick={handleSubmit} 
                            disabled={!isCurrentAnswered} // 🔒 LOCKED until answered
                            className={`px-8 py-4 rounded-xl font-bold transition-all shadow-lg ${
                                isCurrentAnswered 
                                ? "bg-indigo-600 text-white shadow-indigo-600/20 hover:bg-indigo-700 hover:-translate-y-1" // ✅ Changed from Green to Indigo
                                : "bg-slate-200 text-slate-400 cursor-not-allowed"
                            }`}
                        >
                            Submit Exam <i className="fa-solid fa-check ml-2"></i>
                        </button>
                        )}
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default ActiveExam;
